/**
 * Searching and labelling for the results table.
 *
 * Kept out of the component so the matching rules are testable — a 900-row run is
 * exactly where a subtly wrong search is hardest to notice.
 */

import type { RowResult } from "./runUpload.js";

/**
 * Free-text match across the three columns an operator would search by: the company
 * name, the company UUID, and the reference value.
 *
 * Case-insensitive and substring-based, so a partial UUID or a partial firm name both
 * work. The outcome and detail columns are deliberately excluded — filtering by outcome
 * is what the chips are for, and including `detail` would make a search for "32" match
 * every row whose detail happens to mention it.
 */
export function matchesSearch(result: RowResult, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") {
    return true;
  }
  return (
    result.row.label.toLowerCase().includes(needle) ||
    result.row.companyUuid.toLowerCase().includes(needle) ||
    result.row.referenceValue.toLowerCase().includes(needle)
  );
}

/**
 * Human label for a reference type, used as the value column's header.
 *
 * Derived rather than hardcoded: the reference type is a field on the page, so a fixed
 * "Mondaq ID" heading would be wrong the moment a second type is offered. Unknown types
 * fall back to title-casing the key, which reads acceptably for anything snake_cased.
 */
export function referenceTypeLabel(referenceType: string): string {
  const known: Record<string, string> = {
    mondaq_id: "Mondaq ID",
  };
  if (known[referenceType]) {
    return known[referenceType];
  }
  return referenceType
    .split("_")
    .map(part => (part.length <= 2 ? part.toUpperCase() : part[0]?.toUpperCase() + part.slice(1)))
    .join(" ");
}
